import { getGlobalLogger } from '@zg-rentals/logger-base';
import { getGlobalMonitor } from '@zg-rentals/monitor-base';

// ensures all counts are scoped to specific namespace
export function getCountName(identifier: string) {
  return `analytics ${identifier}`;
}

export function getLogPrefix(message: string) {
  return `rentals-analytics: ${message}`;
}

// ensures all errors metrics are scoped to specific namespace
export function getAnalyticsErrorType(identifier: string) {
  return `RentalsAnalyticsError__${identifier}`;
}

export function instrumentTracker(type: string, payload: unknown) {
  // for viewing/debugging events locally
  getGlobalLogger()?.debug(getLogPrefix(`${type} event fired`), { payload });
  // for analyzing/debugging tracker calls in the wild
  getGlobalMonitor()?.count({ name: getCountName(`${type} total`) });
}

export function instrumentHandler(type: string) {
  return (id: string) => getGlobalMonitor().count({ name: `${getCountName(type)} ${id}` });
}

// prevents crashes from accidental ssr or iso invocations
export function clientOnly<TArgs extends Array<unknown>, TReturn>(
  fn: (...args: TArgs) => TReturn,
): (...args: TArgs) => TReturn | undefined {
  return (...args: TArgs) => {
    if (typeof window === 'undefined') {
      // this is likely from legacy apps calling analytics code from their isomorphic code
      getGlobalMonitor()?.count({ name: getCountName('clientOnlyCalledOnServer') });

      return;
    }

    return fn(...args);
  };
}

// does not URL-decode cookies like js-cookie lib; what you see is what you get
export function getCookie(name: string) {
  const value = '; ' + document.cookie;
  const parts = value.split('; ' + name + '=');
  if (parts.length === 2) return parts.pop()?.split(';').shift();
}

// example unparsed encoded zuid: %22X1-ZU-17wcgdezazzv_8mfo2%22
// however we've sometimes seen extra leading/trailing encoded characters (%22, %5C, etc)
// we know all encoded zuids begin with with X1, so grab that and split off the excess
export function parseEncodedZuid(encodedZuid?: string) {
  return encodedZuid?.split(/(?=X1)/)[1]?.split('%')[0];
}

export type ConsentWindow = Window & {
  'disable-analytics'?: boolean;
};

// set from DataGrail consent by page frame or initZanalytics; the SDK no-ops every call while it's true
export function isAnalyticsDisabled() {
  return Boolean((window as ConsentWindow)['disable-analytics']);
}
